import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useAppBranding } from 'src/components/AppBrandingProvider/AppBrandingProvider';
import { Redirect } from 'react-router-dom';
import { useFeatureFlag, useIsLoggedIn } from 'obt-common';
import { injectRedirectURLQueryParam } from 'src/utils/injectRedirectURLQueryParam';
import { injectLogoutQueryParam } from '../../../utils/injectLogoutQueryParam';

/**
 *
 * It is used when we have external website (not spotnana) as homepage URL then we have to redirect there
 */
export function useHomeLocation(): { url: string; type: 'external' | 'internal' } {
  const { appHomeRoute, externalHomePageUrl } = useAppBranding();
  let url: string;
  let type: 'external' | 'internal';

  if (externalHomePageUrl && externalHomePageUrl.length) {
    url = externalHomePageUrl;
    type = 'external';
  } else {
    url = appHomeRoute;
    type = 'internal';
  }

  return { url, type };
}

export function useHomeRedirect(): (options?: { shouldReplaceRoute?: boolean }) => void {
  const { url, type } = useHomeLocation();
  const history = useHistory();
  return useCallback(
    ({ shouldReplaceRoute = false } = {}) => {
      if (type === 'internal') {
        if (shouldReplaceRoute) {
          history.replace({ pathname: url });
        } else {
          history.push({ pathname: url });
        }
      } else {
        window.location.assign(url);
      }
    },
    [history, type, url],
  );
}

export function HomeRedirect() {
  const { url, type } = useHomeLocation();
  const redirect = useHomeRedirect();
  const hasRedirectedRef = useRef(false);
  useEffect(() => {
    if (!hasRedirectedRef.current && type === 'external') {
      redirect();
      hasRedirectedRef.current = true;
    }
  }, [redirect, type]);

  if (type === 'internal') {
    return <Redirect to={url} />;
  }

  return null;
}

type LOGOUT_REDIRECTION_STATE = 'UNDETERMINED' | 'REDIRECTING' | 'HOME_REDIRECTION';

export function useCustomHomeRedirect(): LOGOUT_REDIRECTION_STATE {
  const { logoutRedirectUrl } = useAppBranding();
  const isUserLoggedIn = useIsLoggedIn();
  const [redirectionState, setRedirectionState] = useState<LOGOUT_REDIRECTION_STATE>('UNDETERMINED');
  const hasRedirectedRef = useRef(false);
  const isAuthDynamicRedirectURLEnabled = useFeatureFlag('FE_CORP_AUTH_DYNAMIC_REDIRECT_URL');
  const isLogoutQueryParamEnabled = useFeatureFlag('FE_CORP_LOGOUT_QUERY_PARAM') ?? false;

  useEffect(() => {
    if (!isUserLoggedIn) {
      if (logoutRedirectUrl && !hasRedirectedRef.current) {
        const redirectUrlWithRedirectQueryParam = isAuthDynamicRedirectURLEnabled
          ? injectRedirectURLQueryParam(logoutRedirectUrl)
          : logoutRedirectUrl;
        const redirectUrlWithLogoutQueryParam = isLogoutQueryParamEnabled
          ? injectLogoutQueryParam(redirectUrlWithRedirectQueryParam)
          : redirectUrlWithRedirectQueryParam;

        window.location.assign(redirectUrlWithLogoutQueryParam);
        hasRedirectedRef.current = true;
        setRedirectionState('REDIRECTING');
      } else {
        setRedirectionState('HOME_REDIRECTION');
      }
    } else {
      setRedirectionState('HOME_REDIRECTION');
    }
  }, [isAuthDynamicRedirectURLEnabled, isLogoutQueryParamEnabled, isUserLoggedIn, logoutRedirectUrl]);

  return redirectionState;
}
